export const BUSINESS_TRAINING_PAGE_PATH = "/business-training/";
export const BUSINESS_TRAINING_RESOURCE_PATH =
  "/resources/clinic-team-and-treatment-pathway-planner/";

export const BUSINESS_TRAINING_FAQS = [
  {
    question: "Can you help us plan training for more than one team member?",
    answer:
      "Yes. These calls are built for owners and managers deciding where training budget should go across a team, not for one-off learner enquiries.",
  },
  {
    question: "Do you only work with clinics?",
    answer:
      "No. We also work with salons and other beauty businesses that are building team capability or adding new treatment lines.",
  },
  {
    question: "Can you help us decide who should train first?",
    answer:
      "Yes. A core part of the conversation is deciding whether the right investment is an existing team member, a new hire, or a different route entirely.",
  },
  {
    question:
      "Do we need to know exactly which course we want before speaking to you?",
    answer:
      "No. Many businesses speak to us before that point because they want to avoid committing budget in the wrong order or backing the wrong route.",
  },
  {
    question: "Can we discuss launching a new treatment on the call?",
    answer:
      "Yes. If you are planning a new treatment line, the conversation can focus on commercial fit, ownership inside the team, and what needs to happen before launch.",
  },
  {
    question: "Can we discuss dates and group options?",
    answer:
      "Yes. Once the commercial decision is clearer, we can talk through timing, seat planning, and the most sensible next booking step for the business.",
  },
] as const;

export const BUSINESS_TRAINING_OFFERS = [
  {
    title: "Clinic Team Capability Review",
    summary:
      "Work out where training budget should go first so you stop buying disconnected courses and start building a stronger team.",
    bullets: [
      "Choose who should train first",
      "Spot sequencing and prerequisite gaps early",
      "Create a clearer capability plan for the next quarter",
    ],
    cta: "Book a Team Capability Call",
  },
  {
    title: "New Treatment Launch Planning Call",
    summary:
      "Pressure-test the training route behind your next treatment launch before you invest in equipment, marketing, or the wrong person.",
    bullets: [
      "Check who should own the new service",
      "Reduce delays caused by missing prerequisites",
      "Build more confidence in the launch decision",
    ],
    cta: "Book a Treatment Launch Call",
  },
] as const;

export const BUSINESS_TRAINING_HERO_VISUALS = [
  {
    title: "Training investment that drives growth",
    src: "/images/training-room.jpg",
    alt: "Cosmetic College training room prepared for a practical training session",
  },
  {
    title: "Upskill teams for higher-value services",
    src: "/images/courses/microneedling.jpg",
    alt: "Advanced skin training session taking place in clinic",
  },
  {
    title: "Expand into new treatment lines",
    src: "/images/courses/foundation-dermal-fillers.jpg",
    alt: "Aesthetic treatment training supporting clinic service expansion",
  },
] as const;

export const BUSINESS_TRAINING_SERVICE_AREAS = [
  {
    title: "Grow advanced skin services",
    image: "/images/courses/microneedling.jpg",
    alt: "Microneedling training session for advanced skin capability",
    description:
      "For owners building stronger skin capability in the team before investing further in higher-value treatment options.",
  },
  {
    title: "Plan laser hair removal properly",
    image: "/articles/vtct-laser-hair-removal-training.jpg",
    alt: "Laser hair removal training equipment and treatment room",
    description:
      "For owners deciding whether laser is the right next move, who should deliver it, and what needs to be in place first.",
  },
  {
    title: "Add a specialist laser service",
    image: "/images/laser-tattoo-removal-training.jpg",
    alt: "Laser tattoo removal training session inside clinic environment",
    description:
      "For owners exploring tattoo removal as a more specialist service with repeat-treatment demand and premium positioning potential.",
  },
  {
    title: "Strengthen injectable progression",
    image: "/images/courses/profhilo.jpg",
    alt: "Injectables training practical session for advanced clinic progression",
    description:
      "For owners planning the next stage of injectable capability without backing the wrong route or the wrong person too early.",
  },
] as const;

export const BUSINESS_TRAINING_PROOF_POINTS = [
  "Clearer decisions on where training budget will create the strongest business return",
  "Stronger planning around team capability, service expansion, and staff progression",
  "Guidance built around commercial fit, not generic course selling",
  "Training decisions that better support compliance, insurance, and future growth",
] as const;

export function buildFaqSchema(
  faqs: ReadonlyArray<{ question: string; answer: string }>,
) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };
}
